import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export { BACKEND_URL };
export const API_BASE = `${BACKEND_URL}/api`;

// Fallback shown on the public site whenever a car has no photos yet
// (e.g. a freshly imported "Verwacht" placeholder car). Lives in
// /app/frontend/public/brand/ so it ships with the static build.
export const PLACEHOLDER_IMAGE = "/brand/coming-soon.jpg";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export function imageUrl(u, opts = {}) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) {
    const base = `${BACKEND_URL}${u}`;
    // Add ?v=thumb for small previews (cards, thumbnails) — backend returns
    // a ~600px JPEG (~50 KB) instead of the full 1920px (~330 KB).
    if (opts.thumb) {
      return base.includes("?") ? `${base}&v=thumb` : `${base}?v=thumb`;
    }
    return base;
  }
  return u;
}

export const carsApi = {
  list: (params = {}) => api.get("/cars", { params }).then((r) => r.data),
  get: (id) => api.get(`/cars/${id}`).then((r) => r.data),
  create: (body) => api.post("/cars", body).then((r) => r.data),
  update: (id, body) => api.put(`/cars/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/cars/${id}`).then((r) => r.data),
  markSold: (id, body = null) => api.post(`/cars/${id}/mark-sold`, body).then((r) => r.data),
  updatePrice: (id, price) => api.patch(`/cars/${id}/price`, { price }).then((r) => r.data),
  updateVisibility: (id, hidden) => api.patch(`/cars/${id}/visibility`, { hidden }).then((r) => r.data),
  translateAll: () => api.post("/admin/translate-all", null, { timeout: 60000 }).then((r) => r.data),
  cleanDescriptions: () => api.post("/admin/clean-descriptions", null, { timeout: 60000 }).then((r) => r.data),
  getJob: (jobId) => api.get(`/admin/jobs/${jobId}`).then((r) => r.data),
};

export const dossierApi = {
  // Public (customer)
  getPublic: (token) => api.get(`/dossier/${token}`).then((r) => r.data),
  addCustomerComment: (token, body) => api.post(`/dossier/${token}/comment`, body).then((r) => r.data),
  setRegistrationChoice: (token, choice) => api.post(`/dossier/${token}/registration-choice`, { choice }).then((r) => r.data),
  setPlateCombination: (token, combination) => api.post(`/dossier/${token}/plate-combination`, { combination }).then((r) => r.data),
  // Admin
  list: () => api.get("/admin/dossiers").then((r) => r.data),
  listByView: (view) => api.get("/admin/dossiers", { params: { view } }).then((r) => r.data),
  archive: (id) => api.post(`/admin/dossiers/${id}/archive`).then((r) => r.data),
  unarchive: (id) => api.post(`/admin/dossiers/${id}/unarchive`).then((r) => r.data),
  get: (id) => api.get(`/admin/dossiers/${id}`).then((r) => r.data),
  patch: (id, body) => api.patch(`/admin/dossiers/${id}`, body).then((r) => r.data),
  completeStep: (id, step) => api.post(`/admin/dossiers/${id}/steps/${step}/complete`).then((r) => r.data),
  reopenStep: (id, step) => api.post(`/admin/dossiers/${id}/steps/${step}/reopen`).then((r) => r.data),
  addExtra: (id, body) => api.post(`/admin/dossiers/${id}/extra-werken`, body).then((r) => r.data),
  patchExtra: (id, itemId, body) => api.patch(`/admin/dossiers/${id}/extra-werken/${itemId}`, body).then((r) => r.data),
  removeExtra: (id, itemId) => api.delete(`/admin/dossiers/${id}/extra-werken/${itemId}`).then((r) => r.data),
  removeDoc: (id, docId) => api.delete(`/admin/dossiers/${id}/documents/${docId}`).then((r) => r.data),
  addAdminComment: (id, body) => api.post(`/admin/dossiers/${id}/comment`, body).then((r) => r.data),
  sendRegistrationInfo: (id) => api.post(`/admin/dossiers/${id}/send-registration-info`).then((r) => r.data),
  remove: (id) => api.delete(`/admin/dossiers/${id}`).then((r) => r.data),
  calendarStatus: () => api.get("/admin/calendar/status").then((r) => r.data),
  calendarConnect: () => api.get("/admin/calendar/connect").then((r) => r.data),
};

export const soldApi = {
  list: () => api.get("/sold-cars").then((r) => r.data),
  create: (body) => api.post("/sold-cars", body).then((r) => r.data),
  remove: (id) => api.delete(`/sold-cars/${id}`).then((r) => r.data),
  restore: (id) => api.post(`/sold-cars/${id}/restore`).then((r) => r.data),
};

export const registerApi = {
  list: () => api.get("/admin/register").then((r) => r.data),
  create: (body) => api.post("/admin/register", body).then((r) => r.data),
  patch: (id, body) => api.patch(`/admin/register/${id}`, body).then((r) => r.data),
  checkin: (id) => api.post(`/admin/register/${id}/checkin`).then((r) => r.data),
  checkout: (id) => api.post(`/admin/register/${id}/checkout`).then((r) => r.data),
  undoCheckout: (id) => api.post(`/admin/register/${id}/undo-checkout`).then((r) => r.data),
  remove: (id) => api.delete(`/admin/register/${id}`).then((r) => r.data),
};

export const bidApi = {
  list: () => api.get("/admin/bid-calcs").then((r) => r.data),
  create: (body) => api.post("/admin/bid-calcs", body).then((r) => r.data),
  update: (id, body) => api.put(`/admin/bid-calcs/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/bid-calcs/${id}`).then((r) => r.data),
  setSoldPrice: (soldId, price) => api.patch(`/admin/sold-cars/${soldId}/price`, { sold_price: price }).then((r) => r.data),
  profitOverview: () => api.get("/admin/bid-calcs/profit-overview").then((r) => r.data),
  bulkRemove: (ids) => api.post("/admin/bid-calcs/bulk-delete", { ids }).then((r) => r.data),
};

export const inquiriesApi = {
  create: (body) => api.post("/inquiries", body).then((r) => r.data),
  list: () => api.get("/inquiries").then((r) => r.data),
  update: (id, body) => api.patch(`/inquiries/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/inquiries/${id}`).then((r) => r.data),
};

export const authApi = {
  exchange: (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await axios.post(`${API_BASE}/upload`, fd, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
};

export default api;
